import React, { useEffect, useRef } from "react";
import { ClerkProvider, useUser, useAuth } from "@clerk/react";
import "./App.css";
import InSpektViewer from "./components/InSpektViewer";
import AcceptInvitation from "./components/AcceptInvitation";
import InSpektLoader from "./components/InSpektLoader";

const API_URL         = process.env.REACT_APP_API_URL || "";
const PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

function AppInner() {
  const { isLoaded, isSignedIn, user } = useUser();
  const { signOut, getToken } = useAuth();
  const loggedRef = useRef(false);

  // Pre-load Cesium.js while Clerk auth resolves
  useEffect(() => {
    if (document.querySelector("script[data-cesium]")) return;
    const css = document.createElement("link");
    css.rel  = "stylesheet";
    css.href = "/cesium/Widgets/widgets.css";
    document.head.appendChild(css);
    const script = document.createElement("script");
    script.src   = "/cesium/Cesium.js";
    script.setAttribute("data-cesium", "1");
    document.head.appendChild(script);
  }, []);

  // Log access once per session
  useEffect(() => {
    if (!isSignedIn || !user || loggedRef.current) return;
    loggedRef.current = true;
    const userId = user.primaryEmailAddress?.emailAddress || user.username || "";
    if (!userId) return;
    getToken().then(token => {
      fetch(`${API_URL}/api/log-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ userId, accessed_at: new Date().toISOString() }),
      }).catch(() => {});
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, user]);

  // Invitation acceptance route
  if (window.location.pathname === "/accept-invitation") {
    if (!isLoaded) return <InSpektLoader />;
    if (!isSignedIn) return <AcceptInvitation />;
    const hasTicket = new URLSearchParams(window.location.search).has("__clerk_ticket");
    if (hasTicket) {
      signOut({ redirectUrl: window.location.href });
      return <InSpektLoader />;
    }
    window.location.href = "/";
    return null;
  }

  if (!isLoaded) return <InSpektLoader />;

  if (isSignedIn) {
    const userId = user.primaryEmailAddress?.emailAddress || user.username || "";
    return (
      <div className="App">
        <InSpektViewer userId={userId} onLogout={() => signOut()} />
      </div>
    );
  }

  // Session expired or not signed in — send to Geospekter portal login
  const portalUrl = process.env.REACT_APP_API_URL || "https://portal.geospekter.com";
  window.location.replace(portalUrl);
  return <InSpektLoader />;
}

function App() {
  if (!PUBLISHABLE_KEY) {
    return (
      <div style={{ padding: 40, fontFamily: "Inter, sans-serif", color: "#dc2626" }}>
        <strong>Missing REACT_APP_CLERK_PUBLISHABLE_KEY</strong>
        <p>Add it to your <code>.env</code> file and restart.</p>
      </div>
    );
  }

  return (
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      signInUrl="/"
      signUpUrl="/accept-invitation"
      afterSignInUrl="/"
      afterSignUpUrl="/"
    >
      <AppInner />
    </ClerkProvider>
  );
}

export default App;
